import { useSyncExternalStore } from "react";
import type { DataRouter } from "react-router";
import { HGStacks } from "@/stack/stacks";
import { createStack } from "@/stack/stack";

let globalStacks: HGStacks | null = null;

// hook serializing global stacks to the session storage if they are changed
const handleGlobalStacksChanged = (router: DataRouter) => () => {
  if (!globalStacks) {
    throw Error("Global stacks are not ready");
  }

  const stacksState = globalStacks.save();

  sessionStorage.setItem("stacks", stacksState);

  document.title = globalStacks.getName();
  if (router.state.location.state?.stacks !== stacksState) {
    router.navigate(".", {
      state: { stacks: stacksState },
      replace: false,
    });
  }
  // todo: maybe move tags invalidation to useEffect (dependend on intervals)
  // store.dispatch(hgApi.util.invalidateTags(["HGDBCounts"]));
};

// initialize stacks, either create from scratch or load from session storage
export const initGlobalStacks = (router: DataRouter) => {
  globalStacks = new HGStacks(createStack(), null);
  const storedStacks = sessionStorage.getItem("stacks");
  if (storedStacks) {
    globalStacks.load(storedStacks);
  }
  globalStacks.subscribe(handleGlobalStacksChanged(router));
  if (import.meta.env.DEV) {
    // Dev only, and only so the time cursor can be driven from outside React:
    // importing this module from the console gets a second instance whose
    // singleton is null, which makes the real one unreachable.
    (window as unknown as Record<string, unknown>).__globalStacks = globalStacks;
  }
};

// subscribe on global stacks updates
export const useGlobalStacks = () => {
  if (!globalStacks) {
    throw Error("Global stacks are not ready");
  }
  useSyncExternalStore(
    (callback) => (globalStacks ? globalStacks.subscribe(callback) : () => {}),
    () => (globalStacks ? globalStacks.getUpdateCounter() : null),
  );

  return globalStacks;
};

// to retreive global stacks outside of components
export default function getGlobalStacks() {
  if (!globalStacks) {
    throw Error("Global stacks are not ready");
  }
  return globalStacks;
}
